import React from 'react'
import { useLang } from '../../lib/useLang'
import './ModInputFile.css'

export default function ModInputFile({
  onChange,
  fileInfo,
  className,
  inputClassName,
  multiple = false,
  maxFileSize = 10_737_418_240,
  fileLimit = 10,
  buttonText,
  existingFiles = [],
  onRemoveExisting,
  ...props
}) {
  const { t } = useLang()
  const inputRef = React.useRef(null)
  const [selectedFiles, setSelectedFiles] = React.useState([])
  const [isDragging, setIsDragging] = React.useState(false)
  const lastFilesRef = React.useRef([])

  // Combined list: existing (restored from DB) + newly selected
  const allFiles = React.useMemo(() => {
    const existing = existingFiles.map(f => ({ name: f.name, size: f.size || 0, _existing: true }))
    const selected = selectedFiles.map(f => ({ name: f.name, size: f.size, _existing: false, _file: f }))
    // Dedupe: if a selected file has same name as existing, keep selected (it's a re-upload)
    const existingNames = new Set(selected.map(f => f.name))
    return [...existing.filter(f => !existingNames.has(f.name)), ...selected]
  }, [existingFiles, selectedFiles])

  React.useEffect(() => {
    if (inputRef.current && selectedFiles.length > 0) {
      const dataTransfer = new DataTransfer()
      selectedFiles.forEach(file => dataTransfer.items.add(file))
      inputRef.current.files = dataTransfer.files
    }
  }, [selectedFiles])

  const handleInputChange = e => {
    const files = e.target.files
    if (!files || files.length === 0) {
      // User cancelled the file dialog — only clear if there are no existing files
      if (existingFiles.length === 0 && selectedFiles.length === 0) {
        onChange?.(e)
      }
      return
    }

    const newFiles = Array.from(files)

    setSelectedFiles(prev => {
      const prevFiles = prev || []
      const uniqueNewFiles = newFiles.filter(
        newFile => !prevFiles.some(existing => existing.name === newFile.name && existing.size === newFile.size)
      )

      const combinedFiles = [...prevFiles, ...uniqueNewFiles]

      if (multiple && combinedFiles.length > fileLimit) {
        alert(t('inputFile.maxFiles', { limit: fileLimit, current: combinedFiles.length }))
        e.target.value = ''
        return prev
      }

      const invalidFiles = []
      for (let i = 0; i < uniqueNewFiles.length; i++) {
        if (uniqueNewFiles[i].size > maxFileSize) {
          invalidFiles.push(uniqueNewFiles[i].name)
        }
      }

      if (invalidFiles.length > 0) {
        const maxSizeDisplay = maxFileSize >= 1073741824
          ? (maxFileSize / 1073741824).toFixed(0) + 'GB'
          : (maxFileSize / 1048576).toFixed(2) + 'MB'
        alert(`${t('inputFile.filesTooLarge', { maxSize: maxSizeDisplay })}\n${invalidFiles.join('\n')}`)
        e.target.value = ''
        return prev
      }

      const filesChanged =
        combinedFiles.length !== prev.length ||
        combinedFiles.some((file, idx) => !prev[idx] || file.name !== prev[idx].name || file.size !== prev[idx].size)

      if (filesChanged) {
        lastFilesRef.current = combinedFiles
        setTimeout(() => {
          if (inputRef.current) {
            const event = new Event('change', { bubbles: true })
            Object.defineProperty(event, 'target', {
              writable: false,
              value: inputRef.current,
            })
            onChange?.(event)
          }
        }, 0)
      }

      return combinedFiles
    })
  }

  const handleButtonClick = () => {
    inputRef.current?.click()
  }

  const handleRemoveFile = indexToRemove => {
    const newFiles = selectedFiles.filter((_, index) => index !== indexToRemove)
    setSelectedFiles(newFiles)

    if (inputRef.current) {
      const dataTransfer = new DataTransfer()
      newFiles.forEach(file => dataTransfer.items.add(file))
      inputRef.current.files = dataTransfer.files

      const event = new Event('change', { bubbles: true })
      Object.defineProperty(event, 'target', {
        writable: false,
        value: inputRef.current,
      })
      onChange?.(event)
    }
  }

  const handleDragOver = e => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = e => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = e => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      const fakeEvent = { target: { files } }
      handleInputChange(fakeEvent)
    }
  }

  const formatFileSize = bytes => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
    return (bytes / 1073741824).toFixed(2) + ' GB'
  }

  return (
    <div className={`mod-input-file-wrapper ${className || ''}`} {...props}>
      {/* Drop Zone */}
      <div
        className={`mod-input-file-dropzone ${isDragging ? 'mod-input-file-dropzone--active' : ''} ${allFiles.length > 0 ? 'mod-input-file-dropzone--has-files' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        <input
          ref={inputRef}
          type='file'
          className='mod-input-file-hidden'
          onChange={handleInputChange}
          multiple={multiple}
        />

        {allFiles.length === 0 ? (
          <div className='mod-input-file-empty'>
            <div className='mod-input-file-icon'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='17,8 12,3 7,8' />
                <line x1='12' y1='3' x2='12' y2='15' />
              </svg>
            </div>
            <div className='mod-input-file-text'>
              <span className='mod-input-file-cta'>{t('inputFile.clickToUpload')}</span>
              <span className='mod-input-file-or'> {t('inputFile.orDragDrop')}</span>
            </div>
            <div className='mod-input-file-hint'>{t('inputFile.formatHint')}</div>
          </div>
        ) : (
          <div className='mod-input-file-preview'>
            <div className='mod-input-file-icon mod-input-file-icon--success'>
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                <polyline points='22,4 12,14.01 9,11.01' />
              </svg>
            </div>
            <div className='mod-input-file-text'>
              <span className='mod-input-file-count'>{t('inputFile.filesUploaded', { count: allFiles.length })}</span>
            </div>
            <div className='mod-input-file-hint'>{t('inputFile.clickToAddMore')}</div>
          </div>
        )}
      </div>

      {/* File List */}
      {allFiles.length > 0 && (
        <div className='mod-input-file-list'>
          {allFiles.map((file, index) => (
            <div key={`${file.name}-${index}`} className='mod-input-file-item'>
              <div className='mod-input-file-item-icon'>
                <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                  <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                  <polyline points='14,2 14,8 20,8' />
                </svg>
              </div>
              <div className='mod-input-file-item-info'>
                <span className='mod-input-file-item-name'>{file.name}</span>
                {file.size > 0 && <span className='mod-input-file-item-size'>{formatFileSize(file.size)}</span>}
              </div>
              <button
                onClick={e => {
                  e.stopPropagation()
                  if (file._existing) {
                    onRemoveExisting?.(file.name)
                  } else {
                    // Find index in selectedFiles
                    const selIdx = selectedFiles.findIndex(f => f.name === file.name && f.size === file.size)
                    if (selIdx !== -1) handleRemoveFile(selIdx)
                    // Also remove from existing/tables if it was already processed
                    onRemoveExisting?.(file.name)
                  }
                }}
                className='mod-input-file-item-remove'
              >
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <line x1='18' y1='6' x2='6' y2='18' />
                  <line x1='6' y1='6' x2='18' y2='18' />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
